"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { Section } from "@/components/ui/Section";
import { Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { cn } from "@/lib/utils";

export type SolutionHubCategory =
  | "workplace"
  | "retail"
  | "food"
  | "transport"
  | "media"
  | "enterprise";

export type SolutionHubSection = "core" | "extended";

export type SolutionHubCard = {
  title: string;
  description: string;
  section: SolutionHubSection;
  ctaText: string;
  ctaLink: string;
  imageAlt: string;
  imageSrc: string;
  /** Optional hero video to use instead of the image thumbnail when available. */
  videoSrc?: string;
  category: SolutionHubCategory;
  searchTerms: string;
};

type FilterKey = "all" | SolutionHubCategory;

const FILTER_LABELS: Record<FilterKey, string> = {
  all: "All",
  workplace: "Workplace",
  retail: "Retail",
  food: "Food & Beverage",
  transport: "Transport",
  media: "Displays & Media",
  enterprise: "Enterprise",
};

const CATEGORY_BADGE_STYLES: Record<SolutionHubCategory, string> = {
  workplace: "bg-blue-500 ring-blue-500/20",
  retail: "bg-amber-500 ring-amber-500/20",
  food: "bg-orange-500 ring-orange-500/20",
  transport: "bg-violet-500 ring-violet-500/20",
  media: "bg-teal-500 ring-teal-500/20",
  enterprise: "bg-slate-600 ring-slate-600/20",
};

const FILTER_KEYS: FilterKey[] = [
  "all",
  "workplace",
  "retail",
  "food",
  "transport",
  "media",
  "enterprise",
];

interface SolutionsHubGridProps {
  solutions: SolutionHubCard[];
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 10l2.5 2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SolutionCard({ solution }: { solution: SolutionHubCard }) {
  return (
    <article className="group flex h-full flex-col rounded-xl overflow-clip border border-gray-200 bg-white">
      <div className="relative">
        <div className="relative aspect-video overflow-hidden">
          {solution.videoSrc ? (
            <video
              className="h-full w-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              poster={solution.imageSrc}
              aria-label={solution.imageAlt}
            >
              <source src={solution.videoSrc} type="video/webm" />
            </video>
          ) : (
            <Image
              src={solution.imageSrc}
              alt={solution.imageAlt}
              title={solution.imageAlt}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            />
          )}
          <Typography
            variant="small"
            as="span"
            className={cn(
              "absolute left-3 top-3 z-0 text-white rounded-full px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider leading-none ring-1 ring-inset",
              CATEGORY_BADGE_STYLES[solution.category]
            )}
          >
            {FILTER_LABELS[solution.category]}
          </Typography>
        </div>
      </div>

      <div className="flex flex-col px-5 pb-5 pt-4">
        <Typography variant="h3" as="h2" className="mb-2 text-lg">
          {solution.title}
        </Typography>

        <Typography variant="small" as="p" className="leading-relaxed text-slate-600">
          {solution.description}
        </Typography>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <Link href={solution.ctaLink} className="inline-flex">
            <NewButton
              variant="linkPrimary"
              size="sm"
              arrow="grow"
              arrowColorClass="stroke-accent"
              className="h-auto w-auto p-0"
              asChild
            >
              {solution.ctaText}
            </NewButton>
          </Link>
        </div>
      </div>
    </article>
  );
}

function SolutionGrid({ items }: { items: SolutionHubCard[] }) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-7 lg:grid-cols-3 lg:gap-8">
      {items.map(solution => (
        <SolutionCard
          key={`${solution.section}-${solution.ctaLink}-${solution.title}`}
          solution={solution}
        />
      ))}
    </div>
  );
}

export function SolutionsHubGrid({ solutions }: SolutionsHubGridProps) {
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showBackTop, setShowBackTop] = useState(false);

  const filterCounts = useMemo(() => {
    const counts: Record<FilterKey, number> = {
      all: solutions.length,
      workplace: 0,
      retail: 0,
      food: 0,
      transport: 0,
      media: 0,
      enterprise: 0,
    };
    for (const solution of solutions) {
      counts[solution.category] += 1;
    }
    return counts;
  }, [solutions]);

  const visibleSolutions = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return solutions.filter(solution => {
      const matchesFilter = activeFilter === "all" || solution.category === activeFilter;
      const haystack =
        `${solution.searchTerms} ${solution.title} ${solution.description}`.toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      return matchesFilter && matchesSearch;
    });
  }, [solutions, activeFilter, searchQuery]);

  const handleFilterChange = useCallback((key: FilterKey) => {
    setActiveFilter(key);
    setSearchQuery("");
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (value.trim()) {
      setActiveFilter("all");
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setShowBackTop(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Section variant="default" background="gray">
      <div className="sticky top-20 z-10 rounded-xl border-b border-gray-200 bg-white px-4">
        <div className="flex flex-col gap-3 py-2 md:flex-row md:items-center md:gap-0 md:py-0">
          <div
            className="flex items-center gap-0 overflow-x-auto scrollbar-none md:min-w-0 md:flex-1"
            role="tablist"
            aria-label="Filter solutions by category"
          >
            {FILTER_KEYS.map(key => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeFilter === key && !searchQuery.trim()}
                onClick={() => handleFilterChange(key)}
                className={cn(
                  "group shrink-0 border-b-2 px-4 py-3 whitespace-nowrap transition-colors md:px-5 md:py-4",
                  activeFilter === key && !searchQuery.trim()
                    ? "border-primary"
                    : "border-transparent hover:border-primary/30"
                )}
              >
                <Typography
                  variant="small"
                  as="span"
                  className={cn(
                    "text-[13px]",
                    activeFilter === key && !searchQuery.trim()
                      ? "font-semibold text-primary"
                      : "font-medium text-slate-600 group-hover:text-primary"
                  )}
                >
                  {FILTER_LABELS[key]}
                </Typography>
                <Typography
                  variant="small"
                  as="span"
                  className={cn(
                    "ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none",
                    activeFilter === key && !searchQuery.trim()
                      ? "bg-blue-50 text-primary"
                      : "bg-slate-100 text-gray-400"
                  )}
                >
                  {filterCounts[key]}
                </Typography>
              </button>
            ))}
          </div>
          <div className="w-full shrink-0 pb-1 md:ml-auto md:w-auto md:pb-0 md:pl-4">
            <label className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 focus-within:border-primary md:py-1.5">
              <span className="text-gray-400">
                <SearchIcon />
              </span>
              <input
                type="search"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Find a solution..."
                className="min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-800 outline-none placeholder:text-gray-400 md:w-44 md:flex-none"
                aria-label="Find a solution"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="pb-16 pt-10">
        {visibleSolutions.length === 0 ? (
          <div className="py-16 text-center">
            <Typography variant="h3" as="h3" className="mb-2">
              No solutions found
            </Typography>
            <Typography variant="body" as="p">
              Try a different search term or browse all solutions.
            </Typography>
          </div>
        ) : (
          <SolutionGrid items={visibleSolutions} />
        )}
      </div>

      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className={cn(
          "fixed bottom-7 right-7 z-[99] flex h-11 w-11 items-center justify-center rounded-full bg-slate-800 text-white shadow-lg transition-all hover:-translate-y-0.5",
          showBackTop ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M8 12V4M4 8l4-4 4 4"
            stroke="white"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </Section>
  );
}
